import { eachDayOfInterval, format, startOfMonth, subMonths } from 'date-fns';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Bar, Brush, ComposedChart, ResponsiveContainer, Tooltip, XAxis, } from 'recharts';
import { Currency } from '@debbie/common/dist/index.js';
import { VoucherTypeIds } from '@debbie/common/dist/economy/vouchers/index.js';
import { selectTenant } from '@debbie/cortex/dist/me/selectors.js';
import { useDepositInsights } from '@debbie/cortex/dist/case-vouchers/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { RadioSelect, Money } from '../index.js';
import THEME from '../../theme.js';
import { Row, Col } from '../Grid/index.js';
import Filter from '../Icons/Filter.js';
import GrowthDisplay from './GrowthDisplay.js';
import { StatStyled } from './Stat.js';
const DepositsStats = () => {
    const defaultTenant = useSelector(selectTenant);
    const lang = useSelector((s) => s.lang);
    const depositsInsightsData = useDepositInsights();
    const depositsInsights = h.hasData(depositsInsightsData)
        ? depositsInsightsData.data
        : null;
    const [selectedCurrency, setSelectedCurrency] = useState(Currency.EUR);
    const [showAdvancedEconomy, setShowAdvancedEconomy] = useState(false);
    useEffect(() => {
        if (!depositsInsights)
            return;
        let newCurrency = Currency.EUR;
        // Tenant currency
        if (defaultTenant && depositsInsights[defaultTenant.currency]) {
            newCurrency = defaultTenant.currency;
        }
        else {
            // A currency from the data set
            const defaultDepositCurrency = Object.keys(depositsInsights)[0];
            if (defaultDepositCurrency)
                newCurrency = defaultDepositCurrency;
        }
        if (newCurrency !== selectedCurrency) {
            setSelectedCurrency(newCurrency);
        }
    }, [depositsInsights, defaultTenant]);
    const economyDeposits = depositsInsights && depositsInsights[selectedCurrency];
    const currencies = depositsInsights ? Object.keys(depositsInsights) : [];
    if (!economyDeposits || economyDeposits.length === 0)
        return null;
    const _eachDayOfInterval = eachDayOfInterval({
        start: startOfMonth(subMonths(new Date(), 1)),
        end: new Date(),
    });
    return (React.createElement(StatStyled, { header: React.createElement("span", null,
            lang.home.deposits,
            ' ',
            React.createElement(Filter, { onClick: () => setShowAdvancedEconomy(!showAdvancedEconomy), style: { cursor: 'pointer', float: 'right' } })) },
        React.createElement(Row, null,
            React.createElement(Col, { size: "md", width: "6" },
                React.createElement("p", null, lang.home.today),
                React.createElement("h3", { className: "this-one" },
                    React.createElement(Money, { amount: dateDepositFilter(economyDeposits, moment().format('YYYY-MM-DD')) }),
                    React.createElement("div", { className: "meta" },
                        React.createElement(GrowthDisplay, { prev: dateDepositFilter(economyDeposits, moment().subtract(1, 'day').format('YYYY-MM-DD')), curr: dateDepositFilter(economyDeposits, moment().format('YYYY-MM-DD')) }),
                        React.createElement("span", null,
                            lang.home.yesterday,
                            ' ',
                            React.createElement(Money, { amount: dateDepositFilter(depositsInsights ? depositsInsights[selectedCurrency] : [], moment().subtract(1, 'day').format('YYYY-MM-DD')) }))))),
            React.createElement(Col, { size: "md", width: "6" },
                React.createElement("p", null, lang.home.thisMonth),
                React.createElement("h3", { className: "this-one" },
                    React.createElement(Money, { amount: dateDepositFilter(economyDeposits, moment().format('YYYY-MM')) }),
                    React.createElement("div", { className: "meta" },
                        React.createElement(GrowthDisplay, { prev: dateDepositFilter(economyDeposits, moment().subtract(1, 'month').format('YYYY-MM')), curr: dateDepositFilter(economyDeposits, moment().format('YYYY-MM')) }),
                        React.createElement("span", null,
                            lang.home.lastMonth,
                            ' ',
                            React.createElement(Money, { amount: dateDepositFilter(economyDeposits, moment().subtract(1, 'month').format('YYYY-MM')) }))))),
            React.createElement(Col, { size: "md", width: "12" },
                React.createElement("div", { style: { height: '120px' } },
                    React.createElement(ResponsiveContainer, null,
                        React.createElement(ComposedChart, { data: _eachDayOfInterval
                                .map((date) => {
                                const day = format(date, 'yyyy-MM-dd');
                                return (economyDeposits.find((d) => d.day === day) ?? {
                                    day,
                                    voucherTypes: [
                                        { amount: 0, voucherTypeId: VoucherTypeIds.DEPOSIT },
                                    ],
                                });
                            })
                                .map((day) => {
                                const types = day.voucherTypes.reduce((acc, type) => {
                                    acc[lang.pluralEconomyVoucherTypeLabels[type.voucherTypeId] || type.voucherTypeId] = type.amount / 100;
                                    return acc;
                                }, {});
                                return {
                                    day: moment(day.day, 'YYYY-MM-DD').format(THEME.DATE_FORMAT_SHORT),
                                    ...types,
                                };
                            }), margin: {
                                top: 10,
                                right: 0,
                                left: 0,
                                bottom: 0,
                            } },
                            React.createElement("defs", null,
                                React.createElement("linearGradient", { id: "colorPv", x1: "0", y1: "0", x2: "0", y2: "1" },
                                    React.createElement("stop", { offset: "5%", stopColor: THEME.COLOR_ACCENT, stopOpacity: 0.8 }),
                                    React.createElement("stop", { offset: "95%", stopColor: THEME.COLOR_ACCENT, stopOpacity: 0.1 }))),
                            React.createElement(Tooltip, null),
                            React.createElement(XAxis, { dataKey: "day", hide: true }),
                            showAdvancedEconomy && economyDeposits && (React.createElement(Brush, { stroke: THEME.COLOR_ACCENT, tickFormatter: (ps) => {
                                    const value = _eachDayOfInterval[ps];
                                    if (value) {
                                        return moment(value).format(THEME.DATE_FORMAT_SHORT);
                                    }
                                    else {
                                        return '-';
                                    }
                                } })),
                            React.createElement(Bar, { dataKey: lang.pluralEconomyVoucherTypeLabels[VoucherTypeIds.DEPOSIT], fillOpacity: 1, fill: "url(#colorPv)" }))))),
            currencies.length > 1 && (React.createElement(Col, { size: "md", width: "12", style: { marginTop: '10px' } }, currencies.map((currency) => (React.createElement(RadioSelect, { label: currency, active: currency === selectedCurrency, onClick: () => setSelectedCurrency(currency), key: currency }))))))));
};
const dateDepositFilter = (deposits, filter) => deposits.reduce((acc, day) => {
    if (day.day.slice(0, filter.length) === filter) {
        acc =
            acc +
                day.voucherTypes.reduce((add, type) => add + type.amount, 0);
    }
    return acc;
}, 0);
export default DepositsStats;
//# sourceMappingURL=DepositsStats.js.map